import { useEffect } from "react";

/**
 * SEO component: updates <head> meta tags, canonical, hreflang, and JSON-LD structured data.
 * No external dependency. Works inside React SPA.
 */
const SEO = ({
  title,
  description,
  canonical,
  ogImage,
  hreflangs,           // { en: "/path", de: "/path" }
  jsonLd,              // array of schema objects OR single object
  noindex = false,
}) => {
  useEffect(() => {
    const SITE_NAME = "LUXERA KaLiMa";
    const FULL_TITLE = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Luxury Waterproof Jewelry · 18K Gold Plated`;
    document.title = FULL_TITLE;

    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => k !== "content" && el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
    };

    const upsertLink = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("link");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      }
    };

    if (description) {
      upsertMeta('meta[name="description"]', { name: "description", content: description });
      upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
      upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }

    upsertMeta('meta[property="og:title"]', { property: "og:title", content: FULL_TITLE });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: FULL_TITLE });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: jsonLd?.["@type"] === "Product" ? "product" : "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });

    const origin = window.location.origin;
    if (canonical) {
      const absCanonical = canonical.startsWith("http") ? canonical : `${origin}${canonical}`;
      upsertLink('link[rel="canonical"]', { rel: "canonical", href: absCanonical });
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: absCanonical });
    }

    if (ogImage) {
      const absImg = ogImage.startsWith("http") ? ogImage : `${origin}${ogImage}`;
      upsertMeta('meta[property="og:image"]', { property: "og:image", content: absImg });
      upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: absImg });
      upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
      upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "1500" });
    }

    upsertMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1" });

    // Remove old hreflang/canonical alternates
    document.head.querySelectorAll('link[rel="alternate"][data-seo="lang"]').forEach((el) => el.remove());
    if (hreflangs) {
      Object.entries(hreflangs).forEach(([lang, path]) => {
        const abs = path.startsWith("http") ? path : `${origin}${path}`;
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        link.setAttribute("href", abs);
        link.setAttribute("data-seo", "lang");
        document.head.appendChild(link);
      });
      // x-default
      const def = document.createElement("link");
      def.setAttribute("rel", "alternate");
      def.setAttribute("hreflang", "x-default");
      def.setAttribute("href", hreflangs.en ? (hreflangs.en.startsWith("http") ? hreflangs.en : `${origin}${hreflangs.en}`) : window.location.href);
      def.setAttribute("data-seo", "lang");
      document.head.appendChild(def);
    }

    // JSON-LD structured data
    document.head.querySelectorAll('script[type="application/ld+json"][data-seo]').forEach((el) => el.remove());
    const schemas = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
    schemas.forEach((schema) => {
      const s = document.createElement("script");
      s.setAttribute("type", "application/ld+json");
      s.setAttribute("data-seo", "true");
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    });
  }, [title, description, canonical, ogImage, JSON.stringify(hreflangs), JSON.stringify(jsonLd), noindex]);

  return null;
};

// Reusable schema builders
export const buildOrganizationSchema = (origin) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "LUXERA KaLiMa",
  "url": origin,
  "logo": `${origin}/logo.png`,
  "description": "Luxury waterproof jewelry, 18K gold plated, hypoallergenic, designed for sensitive skin.",
  "sameAs": [
    "https://www.instagram.com/luxerakalima",
    "https://www.tiktok.com/@luxerakalima",
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "support@luxerakalima.com",
    "availableLanguage": ["English", "German"],
  },
});

export const buildBreadcrumbSchema = (origin, items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((it, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": it.name,
    "item": `${origin}${it.path}`,
  })),
});

export const buildProductSchema = (origin, product, lang = "en") => {
  const name = lang === "de" ? product.name_de : product.name_en;
  const desc = lang === "de" ? product.description_de : product.description_en;
  const slug = lang === "de" && product.slug_de ? product.slug_de : product.slug;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "image": product.images.map((img) => `${origin}${img}`),
    "description": (desc || "").replace(/\*\*/g, "").slice(0, 5000),
    "brand": { "@type": "Brand", "name": "LUXERA KaLiMa" },
    "sku": product.ref,
    "mpn": product.ref,
    "category": product.category === "necklaces" ? "Jewelry > Necklaces" : "Jewelry > Bracelets",
    "offers": {
      "@type": "Offer",
      "url": `${origin}/product/${slug}`,
      "priceCurrency": product.currency || "EUR",
      "price": product.price.toFixed(2),
      "availability": product.in_stock ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
      "itemCondition": "https://schema.org/NewCondition",
      "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10),
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "EUR" },
        "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "DE" },
        "deliveryTime": { "@type": "ShippingDeliveryTime",
          "handlingTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 2, "unitCode": "DAY" },
          "transitTime": { "@type": "QuantitativeValue", "minValue": 3, "maxValue": 5, "unitCode": "DAY" },
        },
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": "DE",
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 30,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn",
      },
    },
    "aggregateRating": product.reviews_count > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": product.rating.toFixed(1),
      "reviewCount": product.reviews_count,
      "bestRating": 5,
      "worstRating": 1,
    } : undefined,
  };
};

export const buildWebsiteSchema = (origin) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "LUXERA KaLiMa",
  "url": origin,
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${origin}/shop?q={search_term}`,
    "query-input": "required name=search_term",
  },
});

export default SEO;
